<template>
    <div class="hp">
        <video ref="video" class="hp-video" playsinline controls
            @timeupdate="onTime" @loadedmetadata="onMeta"></video>
        <div v-if="error" class="hp-error">{{ error }}</div>
    </div>
</template>

<script>
import Hls from 'hls.js';

/**
 * HLS 播放器。互動影片問答要老師對著影片挑時間點（VF2）。
 *
 * ⚠⚠ **只播自行上傳轉檔的 HLS。** YouTube 不支援（需求方 2026-09-14）——
 * 它要嵌 iframe API、沒有 `duration_ms`、而且隨時可能被下架或插廣告。
 * 後端 `assertMainVideoPlayable` 也會擋，這裡不必再判斷一次。
 *
 * ⚠⚠ **兩條播放路徑都要有。** Safari 原生支援 HLS（`canPlayType`），
 * Chrome / Firefox 不支援、要靠 `hls.js` 把 m3u8 轉成 MSE。只做其中一條的症狀是
 * 「在我的機器上好好的」——開發者用 Mac Safari 測，老師用 Chrome 就是黑畫面。
 *
 * ⚠ `hls.js` 的實例**一定要 destroy**。它會持續拉分段，元件關掉不清就是背景流量
 * 一直跑，而且換影片時舊實例會跟新的搶同一個 `<video>`。
 */
export default {
    name: 'HlsPlayer',
    props: {
        /** `.m3u8` 的網址。空字串 = 還沒選影片。 */
        src: { type: String, default: '' },
    },
    data() {
        return { hls: null, error: '', currentTime: 0, duration: 0 };
    },
    watch: {
        src: {
            immediate: true,
            handler() {
                /// ⚠ `immediate` 時 `$refs.video` 還不存在，要等 DOM 好了再接
                this.$nextTick(() => this.attach());
            },
        },
    },
    beforeDestroy() {
        this.detach();
    },
    methods: {
        attach() {
            this.detach();
            this.error = '';
            const video = this.$refs.video;
            if (!video || !this.src) {
                return;
            }
            /// Safari：原生就吃 m3u8，掛 hls.js 反而會衝突
            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = this.src;
                return;
            }
            if (!Hls.isSupported()) {
                this.error = this.$t('unit.vq.player_unsupported');
                return;
            }
            const hls = new Hls();
            this.hls = hls;
            hls.on(Hls.Events.ERROR, (event, data) => {
                /// ⚠ 只有 `fatal` 才顯示給老師。hls.js 會丟很多可自行復原的錯
                /// （單一分段載入失敗之類），那些顯示出來只會讓人以為壞了。
                if (data && data.fatal) {
                    this.error = this.$t('unit.vq.player_error');
                }
            });
            hls.loadSource(this.src);
            hls.attachMedia(video);
        },
        detach() {
            if (this.hls) {
                this.hls.destroy();
                this.hls = null;
            }
            const video = this.$refs.video;
            if (video) {
                video.removeAttribute('src');
                video.load();
            }
        },
        onTime() {
            const video = this.$refs.video;
            if (!video) {
                return;
            }
            this.currentTime = video.currentTime;
            /**
             * ⚠⚠ **發出去的是整數秒**，停點一律對齊整秒（需求方 2026-09-14）。
             *
             * ⚠ 外面**只能用這個值**，不要另外去讀 `video.currentTime`：
             * `timeupdate` 大約每 250ms 才發一次，兩者會差到一秒。按鈕上寫著
             * 「在 0:30 新增停點」卻建在 0:29，老師找不出哪裡不對。
             */
            this.$emit('time', Math.floor(video.currentTime));
        },
        onMeta() {
            const video = this.$refs.video;
            if (!video) {
                return;
            }
            /// ⚠ 直播或壞掉的 manifest 會是 Infinity，那算不出時間軸
            this.duration = Number.isFinite(video.duration) ? video.duration : 0;
            this.$emit('duration', this.duration);
        },
        /** 讓外面把播放頭移到某一秒（點停點時用）。 */
        seek(seconds) {
            const video = this.$refs.video;
            if (video && Number.isFinite(seconds)) {
                video.currentTime = Math.max(0, seconds);
            }
        },
        /**
         * 暫停。新增停點時要把畫面凍結在當下那一幀——老師是看著畫面決定
         * 「就在這裡問」，影片繼續跑的話他確認不了自己選對了位置。
         */
        pause() {
            const video = this.$refs.video;
            if (video && !video.paused) {
                video.pause();
            }
        },
    },
};
</script>

<style scoped>
.hp { position: relative; background: #000; border-radius: 4px; overflow: hidden; }
.hp-video { display: block; width: 100%; max-height: 360px; }
.hp-error {
    position: absolute; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, .72); color: #fff; font-size: 12px; padding: 6px 10px;
}
</style>
